import { useState } from 'react';
import type { BossMatrixCharacter, IncomeResponse } from '../../lib/bosses';
import { formatCrystalValue, groupBossesByTier, type BossTier } from '../../lib/bossGrouping';
import type { BossDto, ClassDto } from '../../lib/reference';
import { classDisplayName } from '../../lib/reference';
import { BossCell } from './BossCell';
import { BossIcon } from './BossIcon';
import { CapMeter } from './CapMeter';

interface BossMatrixMobileProps {
  characters: BossMatrixCharacter[];
  /** REV18: for friendly class names in the accordion header microtext — falls back to the raw
   *  slug when the catalog hasn't loaded yet or a slug is unrecognized. */
  classes: ClassDto[];
  bossCatalog: BossDto[];
  income: IncomeResponse | undefined;
  editableForTier: (tier: BossTier) => boolean;
  periodKeyForTier: (tier: BossTier) => string;
  planPendingKey: string | null;
  clearPendingKey: string | null;
  /** `5` on a Heroic account, `1` otherwise — see `BossCellProps.crystalMultiplier`. */
  crystalMultiplier: number;
  onSetDifficulty: (character: BossMatrixCharacter, boss: BossDto, difficulty: string) => void;
  onSetCleared: (character: BossMatrixCharacter, boss: BossDto, periodKey: string, cleared: boolean) => void;
  onSetCrystalSold: (character: BossMatrixCharacter, boss: BossDto, periodKey: string, crystalSold: boolean) => void;
}

/**
 * Mobile (<md): one accordion card per character — a header with the 14/week meter and this
 * character's income, expanding to the same tier-grouped boss list (and the same `BossCell`
 * controls) the desktop grid uses. The account summary bar stays pinned above this, in `Bosses.tsx`.
 */
export function BossMatrixMobile({
  characters,
  classes,
  bossCatalog,
  income,
  editableForTier,
  periodKeyForTier,
  planPendingKey,
  clearPendingKey,
  crystalMultiplier,
  onSetDifficulty,
  onSetCleared,
  onSetCrystalSold,
}: BossMatrixMobileProps) {
  const [expanded, setExpanded] = useState<number | null>(characters[0]?.characterId ?? null);
  const groups = groupBossesByTier(bossCatalog);

  if (characters.length === 0) {
    return (
      <p className="text-sm text-text-muted">Add a character under Characters to start tracking bosses.</p>
    );
  }

  return (
    <div className="space-y-3">
      {characters.map((character) => {
        const stats = income?.characters.find((c) => c.characterId === character.characterId);
        const isOpen = expanded === character.characterId;
        return (
          <section key={character.characterId} className="rounded-xl border border-border bg-bg-elevated">
            <button
              type="button"
              onClick={() => setExpanded(isOpen ? null : character.characterId)}
              aria-expanded={isOpen}
              className="flex min-h-tap w-full items-center justify-between gap-3 px-4 py-3 text-left"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-text">{character.name}</p>
                <p className="truncate text-xs text-text-faint">
                  {classDisplayName(character.classSlug, classes)} · Lv.{character.level}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <CapMeter
                  label="Crystals"
                  current={stats?.weeklyCrystalsSold ?? 0}
                  cap={stats?.characterCap ?? 14}
                  reached={stats?.characterCapReached ?? false}
                  size="sm"
                />
                <span className="text-xs font-semibold tabular-nums text-crystal">
                  {formatCrystalValue(stats?.totalMesos ?? 0)}
                </span>
                <span aria-hidden="true" className="text-text-faint">
                  {isOpen ? '▾' : '▸'}
                </span>
              </div>
            </button>

            {isOpen && (
              <div className="space-y-4 border-t border-border px-4 py-3">
                {groups.map((group) => {
                  // Hidden bosses are omitted from the mobile list entirely (no dash placeholder —
                  // desktop's grid keeps a dash so the column stays aligned; a card list has no
                  // such alignment to preserve).
                  const visibleBosses = group.bosses.filter(
                    (boss) => !character.hiddenBossSlugs.includes(boss.slug),
                  );
                  if (visibleBosses.length === 0) {
                    return null;
                  }
                  return (
                    <div key={group.tier}>
                      <h3 className="mb-2 font-display text-xs font-semibold uppercase tracking-widest text-text-faint">
                        {group.label}
                      </h3>
                      <ul className="space-y-2">
                        {visibleBosses.map((boss) => {
                          const periodKey = periodKeyForTier(boss.tier);
                          const editable = editableForTier(boss.tier);
                          const plan = character.plans.find((p) => p.bossSlug === boss.slug);
                          const clear = character.clears.find(
                            (c) => c.bossSlug === boss.slug && c.periodKey === periodKey,
                          );
                          return (
                            // Stacked, not side-by-side (2026-08-16): BossCell's own select now shows
                            // the full difficulty name and can run ~170px wide, which doesn't leave
                            // enough room next to the boss name in one row at a 375px viewport — so
                            // the row wraps to name-above-controls instead of squeezing both sideways.
                            <li
                              key={boss.slug}
                              className="flex flex-col gap-1.5 rounded-lg border border-border bg-bg-elevated-hover px-3 py-2"
                            >
                              <span className="flex items-center gap-2 text-sm text-text-muted">
                                <BossIcon slug={boss.slug} name={boss.name} />
                                <span className="truncate">{boss.name}</span>
                              </span>
                              <BossCell
                                boss={boss}
                                character={character}
                                characterName={character.name}
                                plan={plan}
                                clear={clear}
                                editable={editable}
                                planPending={planPendingKey === `${character.characterId}:${boss.slug}`}
                                clearPending={clearPendingKey === `${character.characterId}:${boss.slug}`}
                                crystalMultiplier={crystalMultiplier}
                                onSetDifficulty={(difficulty) => onSetDifficulty(character, boss, difficulty)}
                                onSetCleared={(cleared) => onSetCleared(character, boss, periodKey, cleared)}
                                onSetCrystalSold={(sold) => onSetCrystalSold(character, boss, periodKey, sold)}
                              />
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
